import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { orderBy } from 'lodash';
import { useUnload } from '../../../utils/custom-hooks';
import { selectors as groupSelectors } from '../../../state/modules/groups';
import { selectors as userSelectors } from '../../../state/modules/users';
import { selectors as membershipSelectors } from '../../../state/modules/memberships';
import { selectors as skillSelectors } from '../../../state/modules/skills';
import { selectors as availabilityCodeSelectors } from '../../../state/modules/availability-codes';
import { selectors as functionSelectors } from '../../../state/modules/standby-duty-functions';
import { operations as dutyTransactionOperations } from '../../../state/modules/duty-transactions';
import { selectors as absenceRequestSelectors } from '../../../state/modules/absence-requests';
import { selectors as dutyExchangeSelectors } from '../../../state/modules/duty-exchanges';
import {
  registerForCombinedScheduleUpdates,
  updateIntervalsInMemory as updateCombinedScheduleIntervalsInMemory,
  updateIntervalsInBackend as updateCombinedScheduleIntervalsInBackend
} from '../../../state/modules/combined-schedules/operations';
import { error as combinedScheduleErrors } from '../../../state/modules/combined-schedules/selectors';
import { fetchCombinedSchedulesWithSeatAllocations } from '../../../state/modules/computer-aided-scheduling/operations';
import { unsavedChangesPresent, selectLoading } from '../../../state/modules/combined-schedules/selectors';
import { splitCombinedScheduleIntervals } from '../../../state/modules/combined-schedules/actions';
import { dutyPlannerSelectors, dutyPlannerOperations } from '../../../state/modules/duty-planner';
import CandidateList from './CandidateList';
import Loader from '../../general/Loader';
import './DutyPlanner.css';
// make sure you include the timeline stylesheet or the timeline will not be styled
import 'react-calendar-timeline/lib/Timeline.css';
import { Row, Col } from '../../ui-kit';
import ScheduleIntervalContextMenu from './ScheduleIntervalContextMenu';

import IntervalCommentsEditModal from './IntervalCommentsEditModal';
import DutyPlannerToolbar from './DutyPlannerToolbar';
import DutyPlannerTimeline from './DutyPlannerTimeline';
import BarLoader from 'react-spinners/BarLoader';
import ScheduleWarningsPopoverConnected from '../warnings/ScheduleWarningsPopoverConnected';
import DashboardAPIManager from '../../dashboards/DashboardAPIManager';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DutyPlanner = ({
  // direct props
  startTime,
  endTime,
  groupId,
  defaultFunctionId,
  readOnly,
  widgetMode,
  // redux props
  dutyPlanner,
  initialLoad,
  skillsById,
  schedulableAvailabilityCodes,
  functionsById,
  usersOffDuty,
  usersOnCall,
  candidateUsers,
  assignedUsersTimelineItems,
  hasUnsavedChanges,
  combinedScheduleLoading,
  combinedScheduleErrors,
  // dispatch
  setDutyPlannerData,
  updateCombinedScheduleIntervalsInMemory,
  updateCombinedScheduleIntervalsInBackend,
  registerForCombinedScheduleUpdates,
  fetchCombinedSchedulesWithSeatAllocations,
  splitCombinedScheduleIntervals,
  fetchDutyTransactions,
  dashboardAPIManager
}) => {
  useEffect(() => {
    dashboardAPIManager = dashboardAPIManager || new DashboardAPIManager();
    dashboardAPIManager.fetchGroupAndSubgroups(groupId);
    dashboardAPIManager.fetchUsers();
    dashboardAPIManager.fetchMemberships();
    dashboardAPIManager.fetchSkills();
    dashboardAPIManager.fetchAvailabilityCodes();
    dashboardAPIManager.fetchStandbyDutyFunctions();
  }, [groupId]);

  useEffect(() => {
    if (combinedScheduleErrors) setUpdating(false);
  }, [combinedScheduleErrors]);

  // any time props change, sync this with the duty planner redux
  useEffect(() => {
    setDutyPlannerData(startTime, endTime, groupId, defaultFunctionId);
  }, [startTime, endTime, groupId, defaultFunctionId]);

  const [periodSelectorTimes, setPeriodSelectorTimes] = useState({
    startTime,
    endTime
  });
  const [commentsDialogIntervalId, setCommentsDialogIntervalId] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    show: false,
    coordX: null,
    coordY: null,
    time: null
  });
  const [plannerMode, setPlannerMode] = useState(defaultFunctionId ? 'duty' : 'on_call');
  // This is used to display a loader when the schedules are being saved
  // saving is usually very fast, nonetheless it is good to have some feedback
  const [isSaving, setSaving] = useState(false);
  const [isUpdating, setUpdating] = useState(false);

  const defaultFunction = functionsById[defaultFunctionId];
  const functionsWithoutDefault = useMemo(() => {
    const allFunctions = Object.values(functionsById);
    const activeFunctions = allFunctions.filter(func => func.group_ids.includes(groupId) && func.active);

    const withoutDefault = activeFunctions
      .filter(func => func.id !== defaultFunctionId)
      .map(func => ({ ...func, title: func.name }));

    return orderBy(withoutDefault, 'display_order');
  }, [defaultFunctionId, functionsById]);

  useUnload(event => {
    // Warn the users about unsaved changes if they atempt to leave
    // the page
    if (!hasUnsavedChanges) return;
    event.returnValue = true;
  });

  // register the beforeunload only once!
  // useEffect(() => window.addEventListener('beforeunload', onUnload), []);

  const assign = useCallback(
    (functionId, user, clearGaps) => {
      const func = functionId || defaultFunctionId;
      const intervalModifications = user.intervals.map(interval => ({
        ...interval,
        start_time: interval.start_time,
        end_time: interval.end_time,
        assigned_function_ids: [func]
      }));

      // Apply the update to redux without storing in backend
      updateCombinedScheduleIntervalsInMemory(
        user.membershipId,
        'membership',
        intervalModifications,
        null,
        null,
        clearGaps
      );
    },
    [defaultFunctionId]
  );

  const findItemById = itemId => assignedUsersTimelineItems.find(i => i.id === itemId);

  // reference to the current websocket subscription
  const subscriptionRef = useRef(null);

  useEffect(() => {
    // Whenever the duty planner period changes, we fetch new schedules if
    // necessary
    // NOTE: If the Duty planner is a widget, It should react to any change in
    // the dashboard datetime, which is passed as a prop from the widget
    // wrapper. This is achieved through the useEffect which synchronizes
    // these props with the duty planner Redux values
    const { groupId, startTime, endTime } = dutyPlanner;
    if (!startTime || !endTime) return; // happens on boot when undefined

    const lookAhead = moment(endTime).diff(startTime, 'seconds');

    // Cancel old subscription if exists
    if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
    // Fetch the new schedules and register to websockets for real time
    // updates
    subscriptionRef.current = registerForCombinedScheduleUpdates({
      groupIds: [groupId],
      type: 'membership',
      look_ahead: lookAhead,
      start_time: startTime
    });

    // Fetch the new absence requests
    fetchDutyTransactions({
      startTime,
      endTime,
      forceFetch: true,
      params: { group_ids: groupId }
    });
  }, [dutyPlanner.startTime, dutyPlanner.endTime]);

  const updateIntervalById = useCallback(
    (intervalId, newAttributes) => {
      const item = findItemById(intervalId);
      if (!item) return;
      storeInterval({ ...item, ...newAttributes });
    },
    [assignedUsersTimelineItems]
  );

  // this method is called when an interval is manipulated. It updates the
  // updated period in the in-memory store, which optimistically updates redux,
  // so the timeline should be immediately updated through the
  // assignedUsersTimelineItems selector
  const storeInterval = useCallback((interval, updateStart = null, updateEnd = null) => {
    const membershipId = interval.membership_id;
    updateStart = updateStart || interval.start_time;
    updateEnd = updateEnd || interval.end_time;

    // Apply the update to redux without storing in backend
    updateCombinedScheduleIntervalsInMemory(membershipId, 'membership', [interval], updateStart, updateEnd);
  }, []);

  // This method will change the value of the Period Selector.
  const periodChange = useCallback(values => {
    setPeriodSelectorTimes({
      startTime: moment(values.start_time).format(),
      endTime: moment(values.end_time).format()
    });
  }, []);

  // This method will sync the local state with redux. We do this to prevent
  // a re-fetch of data for every small change to any of the period selector values.
  // Changes to redux will only be triggered when clicking the Apply button.
  const syncPeriodWithDutyPlannerRedux = useCallback(() => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    setDutyPlannerData(periodSelectorTimes.startTime, periodSelectorTimes.endTime);
  }, [hasUnsavedChanges, periodSelectorTimes.startTime, periodSelectorTimes.endTime]);

  // This function moves the duty planner period by one day. This works under the
  // assumption that Groninngen shifts are 24h. This can be later adapted for different periods
  const changeViewingPeriod = days => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    let newStartTime = moment(dutyPlanner.startTime);
    let newEndTime = moment(dutyPlanner.endTime);
    if (days === 'today') {
      days = moment().startOf('day').diff(newStartTime.clone().startOf('day'), 'days');
      // reset the length of the period to 24h.
      newEndTime = newStartTime.clone().add(1, 'day');
    }

    newStartTime = newStartTime.clone().add(days, 'day').format();
    newEndTime = newEndTime.clone().add(days, 'day').format();

    // Update the values of the period selector as well
    setPeriodSelectorTimes({
      startTime: newStartTime,
      endTime: newEndTime
    });

    setDutyPlannerData(newStartTime, newEndTime);
  };

  const setContextMenuShow = useCallback(
    (show, coordX, coordY, intervalId, time) => {
      // avoid unnecessary updates
      if (!show && !contextMenu.show) return;
      setContextMenu({ show, coordX, coordY, intervalId, time });
    },
    [contextMenu.show]
  );

  const openCommentsDialog = useCallback(() => {
    // the context dialog is opened from the context menu. We copy the
    // intervalId to the commentsDialogIntervalId to open it, and pass a
    // reference to the modal.
    setCommentsDialogIntervalId(contextMenu.intervalId);
    setContextMenuShow(false);
  }, [contextMenu.intervalId]);

  const hideCommentsDialog = useCallback(() => setCommentsDialogIntervalId(null), []);

  // Function that saves all of the changes one by one to the database.
  // Returns a promise so the change clearance can happen once with is
  // done
  const processUnsavedChanges = async ({ ignoreWarnings }) => {
    setSaving(true);

    try {
      await updateCombinedScheduleIntervalsInBackend({ ignoreWarnings });
    } finally {
      setSaving(false);
    }
  };

  const processUnsavedChangesRespectWarnings = useCallback(() => processUnsavedChanges({ ignoreWarnings: false }), []);

  // These are the availability codes which can be selected in the context menu.
  // Only those that are associated to the station.
  const selectableAvailabilityCodes = useMemo(
    () => schedulableAvailabilityCodes.filter(c => c.applicable_to_groups.includes(groupId)),
    [schedulableAvailabilityCodes]
  );

  const removeInterval = useCallback(interval => {
    const emptyInterval = {
      ...interval,
      // Remove all assigned functions to clear the shift
      assigned_function_ids: []
    };

    updateCombinedScheduleIntervalsInMemory(
      interval.membership_id,
      'membership',
      [emptyInterval],
      interval.start_time,
      interval.end_time
    );
  }, []);

  // Functions that returns the Combined schedule intervals suggested
  const getAutoSeatAllocations = useCallback(async () => {
    if (hasUnsavedChanges) {
      globalThis.alert(I18n.t('save_states.unsaved_changes_warning'));
      return;
    }

    const { groupId, startTime, endTime } = dutyPlanner;

    setUpdating(true);

    await fetchCombinedSchedulesWithSeatAllocations({
      group_id: groupId,
      start_time: startTime,
      end_time: endTime,
      mode: plannerMode
    });

    setUpdating(false);
  }, [dutyPlanner, hasUnsavedChanges, plannerMode]);

  /*
      DUTY PLANNER LAYOUT
      -----------------------------------------------------
      Toolbar (date selector and navigators)
      -----------------------------------------------------
      Candidate | *Timeline*
      List      |
  */

  if (!initialLoad) return <Loader />;

  const isProcessing = isUpdating || isSaving;
  const isLoading = combinedScheduleLoading;

  // All the items that contain className={'d-print-none'}
  // will be ignored when printing.
  return (
    <>
      <ToastContainer position="top-center" />
      <Row>
        <ScheduleWarningsPopoverConnected
          handleUpdated={() => setSaving(false)}
          handleDismissed={() => setSaving(false)}
        />
        {/* In read-only mode, the user will not interact with the intervals at
        all, we don't need Modals, Context menu or Candidate List */}
        {!readOnly && (
          <React.Fragment>
            <IntervalCommentsEditModal
              show={!!commentsDialogIntervalId}
              onHide={hideCommentsDialog}
              updateIntervalById={updateIntervalById}
              interval={findItemById(commentsDialogIntervalId)}
            />
            <ScheduleIntervalContextMenu
              className="d-print-none"
              show={contextMenu.show}
              coordX={contextMenu.coordX}
              coordY={contextMenu.coordY}
              setShow={setContextMenuShow}
              openCommentsDialog={openCommentsDialog}
              interval={findItemById(contextMenu.intervalId)}
              updateIntervalById={updateIntervalById}
              availabilityCodes={selectableAvailabilityCodes}
              time={contextMenu.time}
              splitInterval={splitCombinedScheduleIntervals}
              removeInterval={removeInterval}
            />
            <Col lg={2} style={{ maxHeight: '700px', overflow: 'auto' }} className="d-print-none">
              <CandidateList
                candidateUsers={candidateUsers}
                usersOffDuty={usersOffDuty}
                usersOnCall={usersOnCall}
                setPlannerMode={setPlannerMode}
                plannerMode={plannerMode}
                assign={assign}
                periodStartTime={dutyPlanner.startTime}
                periodEndTime={dutyPlanner.endTime}
                functions={functionsWithoutDefault}
                defaultFunction={defaultFunction}
              />
            </Col>
          </React.Fragment>
        )}

        <Col lg={readOnly ? 12 : 10}>
          {/* in widget mode, the Toolbar with time navigation should not appear */}
          {!widgetMode && (
            <DutyPlannerToolbar
              startTime={periodSelectorTimes.startTime}
              endTime={periodSelectorTimes.endTime}
              groupId={groupId}
              changeViewingPeriod={changeViewingPeriod}
              periodChange={periodChange}
              syncPeriodWithDutyPlannerRedux={syncPeriodWithDutyPlannerRedux}
              storeChanges={processUnsavedChangesRespectWarnings}
              hasUnsavedChanges={hasUnsavedChanges}
              isSaving={isSaving}
              isProcessing={isProcessing}
              readOnly={readOnly}
              getAutoSeatAllocations={getAutoSeatAllocations}
              isLoading={isLoading}
              defaultFunction={defaultFunction}
            />
          )}
          {(isProcessing || isLoading) && <BarLoader height="4px" width="100%" color="#c30000" />}
          {!isProcessing && !isLoading && <div style={{ height: '4px' }} />}
          {functionsWithoutDefault && (
            <DutyPlannerTimeline
              startTime={dutyPlanner.startTime}
              endTime={dutyPlanner.endTime}
              setContextMenuShow={setContextMenuShow}
              storeInterval={storeInterval}
              readOnly={readOnly}
              dutyPlanner={dutyPlanner}
              functionsWithoutDefault={functionsWithoutDefault}
              assignedUsersTimelineItems={assignedUsersTimelineItems}
              skillsById={skillsById}
              plannerMode={plannerMode}
              defaultFunctionId={defaultFunctionId}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

const mapStateToProps = state => ({
  dutyPlanner: state.dutyPlanner,
  initialLoad:
    groupSelectors.initialLoadComplete(state) &&
    userSelectors.initialLoadComplete(state) &&
    membershipSelectors.initialLoadComplete(state) &&
    skillSelectors.initialLoadComplete(state) &&
    availabilityCodeSelectors.initialLoadComplete(state) &&
    absenceRequestSelectors.initialLoadComplete(state) &&
    dutyExchangeSelectors.initialLoadComplete(state) &&
    functionSelectors.initialLoadComplete(state),
  skillsById: skillSelectors.byId(state),
  schedulableAvailabilityCodes: availabilityCodeSelectors.selectSchedulableAvailabilityCodes(state),
  functionsById: functionSelectors.byId(state),
  usersOffDuty: dutyPlannerSelectors.usersOffDuty(state),
  usersOnCall: dutyPlannerSelectors.usersOnCall(state),
  candidateUsers: dutyPlannerSelectors.candidateUsers(state),
  assignedUsersTimelineItems: dutyPlannerSelectors.assignedUsersTimelineItems(state),
  hasUnsavedChanges: unsavedChangesPresent(state),
  combinedScheduleLoading: selectLoading(state),
  combinedScheduleErrors: combinedScheduleErrors(state)
});

const mapDispatchToProps = {
  setDutyPlannerData: dutyPlannerOperations.setDutyPlannerData,
  updateCombinedScheduleIntervalsInMemory: updateCombinedScheduleIntervalsInMemory,
  updateCombinedScheduleIntervalsInBackend: updateCombinedScheduleIntervalsInBackend,
  registerForCombinedScheduleUpdates,
  fetchCombinedSchedulesWithSeatAllocations: fetchCombinedSchedulesWithSeatAllocations,
  splitCombinedScheduleIntervals,
  fetchDutyTransactions: dutyTransactionOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(DutyPlanner);
